import { Button, Table, TableBody, TableCell, TableHead, TableRow, TextField } from "@mui/material";
import { useState } from "react";
import { type AdminProject, admin } from "../../data/api";
import { useAction, useLoad } from "../../data/data";
import { StatusBadge } from "../../ui/components/StatusBadge";
import {
  Empty,
  Failure,
  Field,
  FormRow,
  Hint,
  Mono,
  PageHeader,
  Section,
} from "../../ui/components/primitives";
import type { TenantPageProps } from "../pages";

// ---
// Projects — the business boundaries a token can name
//
// A data-plane token must name one, so nothing can be issued for this tenant until one exists.
// ---

export function Projects({ tenant, token, scopes }: TenantPageProps): React.JSX.Element {
  const projects = useLoad(() => admin.projects(tenant, token), [tenant, token]);
  const [id, setId] = useState("");

  const action = useAction(() => {
    setId("");
    projects.reload();
  });

  return (
    <>
      <PageHeader title="Projects" />
      {scopes.includes("project:manage") && (
        <Section title="Create a project">
          <FormRow>
            <Field label="Id" sx={{ flex: "1 1 240px", maxWidth: 320 }}>
              <TextField
                size="small"
                value={id}
                onChange={(event) => setId(event.target.value)}
                placeholder="emea"
              />
            </Field>
            <Button
              variant="contained"
              disabled={id === ""}
              onClick={() => action.run(() => admin.createProject(tenant, token, { id, name: id }))}
            >
              Create
            </Button>
          </FormRow>
          <Hint>A lower-kebab slug. It cannot be renamed later.</Hint>
          <Failure error={action.error} />
        </Section>
      )}

      <Section flush title={`Projects · ${projects.data?.length ?? 0}`}>
        <Failure error={projects.error} />
        {projects.data?.length === 0 ? (
          <Empty>No projects yet. Business tokens cannot be minted until one exists.</Empty>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Project</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {projects.data?.map((project: AdminProject) => (
                <TableRow key={project.id} hover>
                  <TableCell>
                    <Mono>{project.id}</Mono>
                  </TableCell>
                  <TableCell>
                    <StatusBadge
                      label={project.status}
                      tone={project.status === "active" ? "success" : "neutral"}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {scopes.includes("project:manage") && (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() =>
                          action.run(() =>
                            admin.setProjectStatus(
                              tenant,
                              token,
                              project.id,
                              project.status === "active" ? "archived" : "active",
                            ),
                          )
                        }
                      >
                        {project.status === "active" ? "Archive" : "Reactivate"}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
    </>
  );
}
